import { useState, useRef, useEffect } from "react";
import { Plus, Settings, Trash2, GripVertical, LayoutGrid, Pin, PinOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FloorPlanZone } from "@/hooks/useFloorPlanZones";
import { cn } from "@/lib/utils";

interface FloorPlanZoneTabsProps {
  zones: FloorPlanZone[];
  selectedZoneId: string | null;
  onSelectZone: (zoneId: string | null) => void;
  onCreateZone: () => void;
  onEditZone: (zone: FloorPlanZone) => void;
  onDeleteZone: (zoneId: string) => void;
  onReorderZones?: (reorderedZones: FloorPlanZone[]) => void;
  onSetDefaultZone?: (zoneId: string | null) => void;
  isEditMode: boolean;
  tableCountByZone: Record<string, number>;
  onMoveTableToZone?: (tableId: string, zoneId: string | null) => void;
  isDraggingReservation?: boolean;
}

export const FloorPlanZoneTabs = ({
  zones,
  selectedZoneId,
  onSelectZone,
  onCreateZone,
  onEditZone,
  onDeleteZone,
  onReorderZones,
  onSetDefaultZone,
  isEditMode,
  tableCountByZone,
  onMoveTableToZone,
  isDraggingReservation = false,
}: FloorPlanZoneTabsProps) => {
  const totalTables = Object.values(tableCountByZone).reduce((a, b) => a + b, 0);
  const unassignedCount = tableCountByZone["unassigned"] || 0;
  const [dragOverZoneId, setDragOverZoneId] = useState<string | null | "unassigned">(null);
  const [draggedZoneId, setDraggedZoneId] = useState<string | null>(null);
  const [dropTargetZoneId, setDropTargetZoneId] = useState<string | null>(null);
  const [reservationDragOverZoneId, setReservationDragOverZoneId] = useState<string | null>(null);

  // Ref for zone switch timeout
  const zoneSwitchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (zoneSwitchTimeoutRef.current) {
        clearTimeout(zoneSwitchTimeoutRef.current);
      }
    };
  }, []);

  // Handle reservation drag over zone tabs (auto-switch zone after short delay)
  const handleReservationDragOver = (e: React.DragEvent, zoneId: string | null) => {
    if (isEditMode || !isDraggingReservation) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    
    // Only set up switch if hovering a different zone
    if (reservationDragOverZoneId !== zoneId) {
      setReservationDragOverZoneId(zoneId);
      
      // Clear any existing timeout
      if (zoneSwitchTimeoutRef.current) {
        clearTimeout(zoneSwitchTimeoutRef.current);
      }
      
      // Auto-switch zone after 400ms hover (enough time to pass through but quick enough to feel responsive)
      zoneSwitchTimeoutRef.current = setTimeout(() => {
        if (zoneId !== selectedZoneId) {
          onSelectZone(zoneId);
        }
      }, 400);
    }
  };

  const handleReservationDragLeave = () => {
    setReservationDragOverZoneId(null);
    // Clear timeout when leaving
    if (zoneSwitchTimeoutRef.current) {
      clearTimeout(zoneSwitchTimeoutRef.current);
      zoneSwitchTimeoutRef.current = null;
    }
  };

  const handleReservationDrop = (e: React.DragEvent, zoneId: string | null) => {
    if (isEditMode || !isDraggingReservation) return;
    e.preventDefault();
    setReservationDragOverZoneId(null);
    // Clear timeout
    if (zoneSwitchTimeoutRef.current) {
      clearTimeout(zoneSwitchTimeoutRef.current);
      zoneSwitchTimeoutRef.current = null;
    }
  };

  // Handle table drag over zone tabs
  const handleTableDragOver = (e: React.DragEvent, zoneId: string | null | "unassigned") => {
    if (!isEditMode) return;
    // Check if this is a table being dragged
    if (e.dataTransfer.types.includes("table-id")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverZoneId(zoneId);
    }
  };

  const handleTableDragLeave = () => {
    setDragOverZoneId(null);
  };

  const handleTableDrop = (e: React.DragEvent, zoneId: string | null) => {
    if (!isEditMode || !onMoveTableToZone) return;
    e.preventDefault();
    setDragOverZoneId(null);
    
    const tableId = e.dataTransfer.getData("table-id");
    if (tableId) {
      onMoveTableToZone(tableId, zoneId);
    }
  };

  // Handle zone tab reordering
  const handleZoneDragStart = (e: React.DragEvent, zoneId: string) => {
    if (!isEditMode) return;
    e.dataTransfer.setData("zone-id", zoneId);
    e.dataTransfer.effectAllowed = "move";
    setDraggedZoneId(zoneId);
  };

  const handleZoneDragOver = (e: React.DragEvent, targetZoneId: string) => {
    if (!isEditMode || !draggedZoneId || draggedZoneId === targetZoneId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetZoneId(targetZoneId);
  };

  const handleZoneDragLeave = () => {
    setDropTargetZoneId(null);
  };

  const handleZoneDrop = (e: React.DragEvent, targetZoneId: string) => {
    if (!isEditMode || !onReorderZones) return;
    e.preventDefault();
    
    const sourceZoneId = e.dataTransfer.getData("zone-id");
    if (!sourceZoneId || sourceZoneId === targetZoneId) {
      setDropTargetZoneId(null);
      setDraggedZoneId(null);
      return;
    }

    // Find indices
    const sourceIndex = zones.findIndex(z => z.id === sourceZoneId);
    const targetIndex = zones.findIndex(z => z.id === targetZoneId);
    
    if (sourceIndex === -1 || targetIndex === -1) return;

    // Reorder zones
    const newZones = [...zones];
    const [removed] = newZones.splice(sourceIndex, 1);
    newZones.splice(targetIndex, 0, removed);

    // Update order_index for all zones
    const reorderedZones = newZones.map((zone, index) => ({
      ...zone,
      order_index: index,
    }));

    onReorderZones(reorderedZones);
    setDropTargetZoneId(null);
    setDraggedZoneId(null);
  };

  const handleZoneDragEnd = () => {
    setDraggedZoneId(null);
    setDropTargetZoneId(null);
  };

  return (
    <div className="flex items-center gap-2 border-b border-border pb-2">
      <ScrollArea className="flex-1">
        <div className="flex items-center gap-1">
          {/* Zone tabs first - main content */}
          {zones.map((zone) => (
            <div 
              key={zone.id} 
              className={cn(
                "flex items-center transition-all",
                draggedZoneId === zone.id && "opacity-50",
                dropTargetZoneId === zone.id && "ring-2 ring-primary rounded-md"
              )}
              draggable={isEditMode}
              onDragStart={(e) => handleZoneDragStart(e, zone.id)}
              onDragOver={(e) => {
                handleZoneDragOver(e, zone.id);
                handleTableDragOver(e, zone.id);
                handleReservationDragOver(e, zone.id);
              }}
              onDragLeave={() => {
                handleZoneDragLeave();
                handleTableDragLeave();
                handleReservationDragLeave();
              }}
              onDrop={(e) => {
                // Check what type of drag it is
                if (e.dataTransfer.types.includes("zone-id")) {
                  handleZoneDrop(e, zone.id);
                } else if (e.dataTransfer.types.includes("table-id")) {
                  handleTableDrop(e, zone.id);
                } else if (isDraggingReservation) {
                  handleReservationDrop(e, zone.id);
                }
              }}
              onDragEnd={handleZoneDragEnd}
            >
              {isEditMode && (
                <GripVertical className="h-3 w-3 text-muted-foreground/50 cursor-grab mr-0.5" />
              )}
              <Button
                variant={selectedZoneId === zone.id ? "default" : "ghost"}
                size="sm"
                onClick={() => onSelectZone(zone.id)}
                className={cn(
                  "shrink-0 gap-2 transition-all",
                  selectedZoneId === zone.id && "ring-2",
                  isEditMode && dragOverZoneId === zone.id && "ring-2 ring-primary scale-105 bg-primary/10",
                  // Highlight when dragging reservation over zone
                  isDraggingReservation && reservationDragOverZoneId === zone.id && "ring-2 ring-green-500 scale-105 bg-green-500/10",
                  isDraggingReservation && selectedZoneId !== zone.id && "animate-pulse"
                )}
                style={{
                  borderLeftColor: zone.color,
                  borderLeftWidth: selectedZoneId === zone.id ? 3 : 0,
                }}
              >
                {zone.is_default && (
                  <Pin className="h-3 w-3 text-primary shrink-0" />
                )}
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: zone.color }}
                />
                {zone.name}
                <Badge variant="secondary" className="text-xs">
                  {tableCountByZone[zone.id] || 0}
                </Badge>
              </Button>

              {isEditMode && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 ml-0.5">
                      <Settings className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onSetDefaultZone && (
                      <>
                        <DropdownMenuItem 
                          onClick={() => onSetDefaultZone(zone.is_default ? null : zone.id)}
                        >
                          {zone.is_default ? (
                            <>
                              <PinOff className="h-4 w-4 mr-2" />
                              Retirer comme vue par défaut
                            </>
                          ) : (
                            <>
                              <Pin className="h-4 w-4 mr-2" />
                              Définir comme vue par défaut
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => onEditZone(zone)}>
                      <Settings className="h-4 w-4 mr-2" />
                      Modifier la zone
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDeleteZone(zone.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Supprimer la zone
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}

          {/* Separator before utility tabs */}
          {zones.length > 0 && (
            <div className="h-4 w-px bg-border mx-1" />
          )}

          {/* All tables tab - smaller, at the end */}
          <Button
            variant={selectedZoneId === null ? "secondary" : "ghost"}
            size="sm"
            onClick={() => onSelectZone(null)}
            className="shrink-0 text-xs h-7"
          >
            <LayoutGrid className="h-3 w-3 mr-1" />
            Tout
            <Badge variant="outline" className="ml-1.5 text-[10px] px-1">
              {totalTables}
            </Badge>
          </Button>

          {/* Unassigned tables tab - drop target to remove from zone */}
          {(unassignedCount > 0 || isEditMode) && (
            <Button
              variant={selectedZoneId === "unassigned" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => onSelectZone("unassigned")}
              onDragOver={(e) => handleTableDragOver(e, "unassigned")}
              onDragLeave={handleTableDragLeave}
              onDrop={(e) => handleTableDrop(e, null)}
              className={cn(
                "shrink-0 text-muted-foreground text-xs h-7 transition-all",
                isEditMode && dragOverZoneId === "unassigned" && "ring-2 ring-primary scale-105 bg-primary/10"
              )}
            >
              Sans zone
              <Badge variant="outline" className="ml-1.5 text-[10px] px-1">
                {unassignedCount}
              </Badge>
            </Button>
          )}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Add zone button */}
      {isEditMode && (
        <Button
          variant="outline"
          size="sm"
          onClick={onCreateZone}
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-1" />
          Zone
        </Button>
      )}
    </div>
  );
};
